"use client";

import { useEffect } from "react";

const INTERACTION_EVENTS = [
  "pointerdown",
  "keydown",
  "scroll",
  "touchstart",
] as const;

/** Fallback for visitors who never interact: load once the page is long idle. */
const IDLE_FALLBACK_MS = 8000;

/**
 * Tawk.to chat widget, injected on the visitor's first interaction (or after a
 * long idle fallback) instead of at page load. The widget is several hundred KB
 * of script, fonts and iframes; loading it on demand keeps it off the critical
 * path and out of the initial main-thread budget on mobile.
 */
export function DeferredTawk({ propertyId }: { propertyId: string }) {
  useEffect(() => {
    let done = false;

    const load = () => {
      if (done) return;
      done = true;
      cleanup();
      const w = window as typeof window & {
        Tawk_API?: object;
        Tawk_LoadStart?: Date;
      };
      w.Tawk_API = w.Tawk_API || {};
      w.Tawk_LoadStart = new Date();
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://embed.tawk.to/${propertyId}`;
      s.charset = "UTF-8";
      s.setAttribute("crossorigin", "*");
      document.body.appendChild(s);
    };

    const cleanup = () => {
      for (const e of INTERACTION_EVENTS)
        window.removeEventListener(e, load);
      clearTimeout(timer);
    };

    const timer = setTimeout(() => {
      if ("requestIdleCallback" in window) window.requestIdleCallback(load);
      else load();
    }, IDLE_FALLBACK_MS);
    for (const e of INTERACTION_EVENTS)
      window.addEventListener(e, load, { once: true, passive: true });

    return cleanup;
  }, [propertyId]);

  return null;
}
